<template>
  <section id="dashboard" class="py-16 bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <h3 class="text-4xl font-bold text-gray-900 mb-4">ผลการดำเนินงานคลัสเตอร์</h3>
        <p class="text-lg text-gray-600">สถิติและข้อมูลสำคัญของเครือข่ายชุมชนในคลัสเตอร์นี้</p>
      </div>

      <!-- Loading State -->
      <div v-if="isSalesLoading" class="bg-white border border-gray-200 rounded-xl p-8 mb-8">
        <div class="flex items-center justify-center text-gray-600">
          <i class="fas fa-spinner fa-spin mr-3 text-emerald-500"></i>
          <span class="font-medium">กำลังโหลดข้อมูลยอดขายจาก API...</span>
        </div>
      </div>

      <!-- Overview Component Integration -->
      <div v-if="!isSalesLoading && organizationData.oms.length > 0">
        <OverviewTab
          :salesData="computedSalesData"
          :organizationData="organizationData"
          :isSalesLoading="isSalesLoading"
          :isLoadingIntegrationData="isLoadingIntegrationData"
          :sieData="sieData"
          :scmData="scmData"
          :appTechData="appTechData"
          @load-sales-data="loadSalesData"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import OverviewTab from '~/pages/cluster/manage/detail/tab/overview/OverviewTab.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useNuxtApp } from '#app'
import { useSalesAggregation } from '~/composables/useSalesAggregation'

// Props - เหลือเฉพาะข้อมูลพื้นฐาน
const props = defineProps({
  clusterId: {
    type: String,
    default: null
  },
  cluster: {
    type: Object,
    default: null
  }
})

// Emits
const emit = defineEmits(['cluster-data-loaded'])

// Nuxt app และ composables
const { $DSS } = useNuxtApp()
const salesAggregation = useSalesAggregation()

// Internal state management
const bills = salesAggregation?.bills || ref([])
const salesByOM = salesAggregation?.salesByOM || ref({})
const salesByVC = salesAggregation?.salesByVC || ref({})
const salesBySubVC = salesAggregation?.salesBySubVC || ref({})
const salesByCluster = salesAggregation?.salesByCluster || ref({})
const getTotalSalesInTHB = salesAggregation?.getTotalSalesInTHB || (() => 0)
const setBills = salesAggregation?.setBills || (() => {})

// Loading states
const isSalesLoading = ref(false)
const isLoadingIntegrationData = ref(false)

// Data states
const organizationData = ref({
  oms: [],
  clusterId: null
})

const sieData = ref([])
const scmData = ref([])
const appTechData = ref([])

// Computed sales data
const computedSalesData = computed(() => {
  try {
    return {
      totalSales: getTotalSalesInTHB ? getTotalSalesInTHB() : 0,
      totalBills: bills?.value?.length || 0,
      salesByOM: salesByOM?.value || {},
      salesByVC: salesByVC?.value || {},
      salesBySubVC: salesBySubVC?.value || {},
      salesByCluster: salesByCluster?.value || {}
    }
  } catch (error) {
    console.error('Error computing sales data:', error)
    return {
      totalSales: 0,
      totalBills: 0,
      salesByOM: {},
      salesByVC: {},
      salesBySubVC: {},
      salesByCluster: {}
    }
  }
})

// Data loading functions
const loadSalesData = async () => {
  try {
    isSalesLoading.value = true
    const targetClusterId = props.clusterId || props.cluster?._id
    if (!targetClusterId) return
    
    const response = await $DSS(`cluster/${targetClusterId}/bills`)
    if (response && response.success && Array.isArray(response.data)) {
      const bills = response.data.map(bill => ({
        id: bill._id || bill.id,
        billNumber: bill.invoiceId || bill.billNumber,
        amount: parseFloat(bill.totalAmount || bill.amount || 0),
        currency: bill.currency || 'THB',
        date: bill.invoiceDate || bill.date || bill.createdAt,
        customerName: bill.customer?.name || bill.customerName || 'ไม่ระบุ',
        status: bill.status || 'pending',
        clusterId: bill.clusterId,
        omId: bill.omId,
        vcId: bill.vcId,
        subVcId: bill.subVcId || null,
        originalData: bill
      }))
      
      setBills(bills)
    }
  } catch (e) {
    console.error('เกิดข้อผิดพลาดในการโหลดข้อมูลยอดขาย:', e)
  } finally {
    isSalesLoading.value = false
  }
}

const loadOrganizationData = async () => {
  try {
    const targetClusterId = props.clusterId || props.cluster?._id
    if (!targetClusterId) return
    
    const response = await $DSS(`cluster/${targetClusterId}`)
    if (response && response.success && response.data?.oms) {
      organizationData.value = {
        oms: response.data.oms || [],
        clusterId: targetClusterId
      }
    } else {
      organizationData.value = { oms: [], clusterId: targetClusterId }
    }
  } catch (error) {
    console.error('เกิดข้อผิดพลาดในการโหลดข้อมูลองค์กร:', error)
    organizationData.value = { oms: [], clusterId: props.clusterId || props.cluster?._id || null }
  }
}

const loadIntegrationData = async () => {
  try {
    isLoadingIntegrationData.value = true
    const targetClusterId = props.clusterId || props.cluster?._id
    if (!targetClusterId) return

    const [sieResponse, scmResponse, appTechResponse] = await Promise.all([
      $DSS(`cluster/${targetClusterId}/sie`),
      $DSS(`cluster/${targetClusterId}/scm`),
      $DSS(`cluster/${targetClusterId}/apptech`)
    ])

    sieData.value = (sieResponse && sieResponse.success) ? sieResponse.data || [] : []
    scmData.value = (scmResponse && scmResponse.success) ? scmResponse.data || [] : []
    appTechData.value = (appTechResponse && appTechResponse.success) ? appTechResponse.data || [] : []
  } catch (error) {
    console.error('เกิดข้อผิดพลาดในการโหลดข้อมูล Integration:', error)
  } finally {
    isLoadingIntegrationData.value = false
  }
}

// Load all data
const loadAllData = async () => {
  const targetClusterId = props.clusterId || props.cluster?._id
  if (!targetClusterId) {
    console.warn('No cluster ID provided to ClusterPerformanceSection')
    return
  }

  console.log('Loading all cluster performance data for:', targetClusterId)
  await Promise.all([
    loadSalesData(),
    loadOrganizationData(),
    loadIntegrationData()
  ])
  
  emit('cluster-data-loaded', {
    computedSalesData: computedSalesData.value,
    organizationData: organizationData.value,
    integrationData: {
      sie: sieData.value,
      scm: scmData.value,
      appTech: appTechData.value
    }
  })
}

// Watch for cluster changes
watch(() => props.clusterId || props.cluster?._id, async (newClusterId) => {
  if (newClusterId) {
    console.log('Cluster ID changed in ClusterPerformanceSection:', newClusterId)
    await loadAllData()
  }
}, { immediate: false })

// Load data on mount
onMounted(async () => {
  await loadAllData()
})
</script>

<style scoped>
/* Performance section specific styles */
.transition-all {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover effects */
.hover\:shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
</style>